import { ExamDtoClient } from "@/api/dto/exam.dto";
import { ExamType } from "@/api/enums/ExamType";
import { RegistrationType } from "@/api/enums/RegistrationType";
import { ComboboxField } from "@/components/molecules/Combobox/Combobox";
import { formatDate } from "@/lib/utils";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@atoms/Form";
import { useFormContext } from "react-hook-form";

const filterByRegistrationType =
  (
    registrationType: RegistrationType,
    allowReportingAfterRegistrationEnded = false
  ) =>
  (exam: ExamDtoClient) =>
    // still time to register
    // if allowReportingAfterRegistrationEnded is true, we check the exam date instead of the registering end date
    // this is done to allow reporting candidate to an exam even if the registration date has passed
    new Date(
      allowReportingAfterRegistrationEnded ? exam.date : exam.registeringEndDate
    ) >= new Date() &&
    // coherent exam type
    ((exam.type === ExamType.INITIAL &&
      [RegistrationType.EXTENSION, RegistrationType.INITIAL].includes(
        registrationType
      )) ||
      (exam.type === ExamType.RENEW &&
        registrationType === RegistrationType.RENEW));

const defaultLabel = (exam: ExamDtoClient) =>
  `${formatDate(exam.date)} (Date limite d'inscription le ${formatDate(
    exam.registeringEndDate
  )} )`;

interface ExamDateComboboxProps {
  fieldName: string;
  exams?: ExamDtoClient[] | null;
  isLoading: boolean;
  isError: boolean;
  registrationType?: RegistrationType;
  forbiddenExamIds?: Set<number>;
  title?: string;
  label?: (exam: ExamDtoClient) => string;
  allowReportingAfterRegistrationEnded?: boolean;
}

const ExamDateCombobox = ({
  fieldName,
  registrationType,
  exams,
  isLoading,
  isError,
  forbiddenExamIds = new Set(),
  title = "Date de passage de l'examen",
  label = defaultLabel,
  allowReportingAfterRegistrationEnded = false,
}: ExamDateComboboxProps) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={fieldName}
      render={() => (
        <FormItem className="w-full">
          <FormLabel>
            <h2>{title}</h2>
          </FormLabel>
          <FormControl>
            <ComboboxField
              displaySearchBox={true}
              options={
                exams
                  ?.filter(
                    registrationType
                      ? filterByRegistrationType(
                          registrationType,
                          allowReportingAfterRegistrationEnded
                        )
                      : () => true
                  )
                  .sort(
                    (exam0, exam1) =>
                      new Date(exam0.date).getTime() -
                      new Date(exam1.date).getTime()
                  )
                  .map((exam) => {
                    return {
                      value: exam.id,
                      disabled: forbiddenExamIds.has(exam.id),
                      label: label(exam),
                    };
                  }) ??
                (isError
                  ? [
                      {
                        label: "Erreur de chargement des dates d'examen",
                        value: undefined,
                        disabled: true,
                      },
                    ]
                  : isLoading
                  ? [
                      {
                        label: "Chargement...",
                        value: undefined,
                        disabled: true,
                      },
                    ]
                  : [])
              }
              placeholder={"-"}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default ExamDateCombobox;
